import { CustomToast } from "@components/common/Alert";
import { CompanionSidebar } from "@components/companion/CompanionSidebar";
import { DSLinkButton } from "@components/design-system/molecules";
import { FirstTimeUserTour } from "@components/FirstTimeUserTour/FirstTimeUserTour";
import { PimsPalRecord } from "@components/pimspal/PimsPalRecord";
import PipNote from "@components/pip/PipNote";
import PipNoteList from "@components/pip/PipNoteList";
import { MinimizedPipOverlay } from "@components/pip/PipSizeControls";
import { Routes } from "@datadog/browser-rum-react/react-router-v6";
import {
  AreYouSureModal,
  OnboardingProvider,
  SuperSoapProvider,
  UploadProgressProvider,
  useAuth,
  useAuthExpiry,
  useResolvedTheme,
  usePreventRecordingInterruptions,
  useVersionCheck,
  useWindowDimensions,
  RoleProvider,
} from "@hooks";
import { isTestEnv } from "@src/constants";
import { useWidgetLayout } from "@src/context/widgetContext";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";
import { Navigate, Route, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import Footer from "./Footer";
import Header from "./Header";
import AppIconImg from "../../assets/imgs/icon-512.png";
import AnalyticsRouter from "../../utils/analytics/AnalyticsRouter";
import UserAccount from "../account/UserAccount";
import PatientAnalytics from "../analytics/PatientAnalytics";
import Attachments from "../attachments/Attachments";
import {
  AuditTrailNote,
  AuditTrailPatient,
  AuditTrailUser,
} from "../audit";
import RegisterConfirm from "../auth/FirstLogin/RegisterConfirm";
import ForgotPassword from "../auth/ForgotPassword";
import ForgotPasswordReset from "../auth/ForgotPasswordReset";
import Login from "../auth/Login";
import Register from "../auth/Register";
import RegisterTeamInvite from "../auth/RegisterTeamInvite";
import SSOError from "../auth/sso/SSOError";
import SSOLogin, { SSODescopeLogin } from "../auth/sso/SSOLogin";
import SSOLogout from "../auth/sso/SSOLogout";
import AutoTemplateForm from "../autotemplates/AutoTemplateForm";
import AutoTemplatesPage from "../autotemplates/AutoTemplatesPage";
import SharedTemplatePage from "../autotemplates/SharedTemplatePage";
import getAppDownloadLink from "../common/AppDownloadLink";
import { Banner, PermanentlyHideableBanner } from "../common/Banner";
import Maintenance from "../common/Maintenance";
import UserDashboard from "../dashboard/UserDashboard";
import DesignSystemTestPage from "../design-system/DesignSystemTestPage";
import SharedExportNote from "../export/SharedExportNote";
import FourOhFour from "../home/FourohFour";
import Inbox from "../inbox/Inbox";
import CopyPatientSelect from "../notebook/CopyPatientSelect";
import KeyPatientInfo from "../notebook/KeyPatientInfo";
import MovePatientSelect from "../notebook/MovePatientSelect";
import Notebook from "../notebook/Notebook";
import CreateNote from "../notes/CreateNote";
import CreateNoteSelect from "../notes/CreateNoteSelect";
import EditNote from "../notes/EditNote";
import Onboarding from "../Onboarding";
import Patients from "../patients/Patients";
import { PipDialog } from "../pip/PipDialog";
import ExistingUserAcceptTeamInvite from "../team/ExistingUserAcceptTeamInvite";
import TeamManagement from "../team/TeamManagement";
import TeamsManagementPage from "../team/TeamsManagementPage";
import EditTypedTemplate from "../templates/EditTemplate";
import NewTypedTemplate from "../templates/NewTemplate";
import TypedTemplates from "../templates/Templates";

export function PrivateRoute({ children }) {
  const { isUserAuthenticated } = useAuth();
  const { shouldPreventInterruptions } =
    usePreventRecordingInterruptions();
  const ldClient = useLDClient();

  useEffect(() => {
    const userUuid = localStorage.getItem("user_uuid");

    if (userUuid) {
      ldClient.identify({
        key: `user-uuid-${userUuid}`,
      });
    }
  }, [ldClient]);

  useEffect(() => {
    if (!isUserAuthenticated && shouldPreventInterruptions) {
      toast.warning(
        <CustomToast message="Your session has expired. Your recording will be saved automatically to your unsynced recordings when you stop recording." />,
        { autoClose: false, toastId: "session-expired-recording" },
      );
    }
  }, [isUserAuthenticated, shouldPreventInterruptions]);

  if (!isUserAuthenticated && !shouldPreventInterruptions) {
    const currentPath = `${window.location.pathname}${window.location.search}`;
    return (
      <Navigate
        to={`/auth/login?next=${encodeURIComponent(currentPath)}`}
      />
    );
  }

  return children;
}

function App(props) {
  useAuthExpiry();
  const { isUpdateAvailable } = useVersionCheck();

  const mainClassNameOriginal =
    "flex flex-col grow w-11/12 md:w-[85%] self-center justify-start";
  const { width } = useWindowDimensions();
  const location = useLocation();
  const resolvedTheme = useResolvedTheme();
  const {
    shouldShowAreYouSureModal,
    hideAreYouSureModal,
    confirmAction,
    shouldPreventInterruptions,
  } = usePreventRecordingInterruptions();

  const {
    isWebInMaintenanceMode,
    useLegacyNotes,
    allowCompanionOptIn,
    releaseWebEnableFtueTour,
  } = useFlags();
  const { isAppHeaderVisible } = useWidgetLayout();

  const inPipMode = location.pathname.startsWith("/pip");
  const inPimsPalRoute = location.pathname.startsWith("/pimspal");

  useEffect(() => {
    const el = window.document.documentElement;
    el.classList.remove(
      "dark",
      "accessible-light",
      "accessible-dark",
    );
    if (resolvedTheme === "dark") {
      el.classList.add("dark");
    } else if (resolvedTheme === "accessible-light") {
      el.classList.add("accessible-light");
    } else if (resolvedTheme === "accessible-dark") {
      el.classList.add("dark", "accessible-dark");
    }
    // "light" → no classes
  }, [resolvedTheme]);

  // If maintenance mode is enabled and we're not preventing interruptions, show maintenance screen
  if (isWebInMaintenanceMode && !shouldPreventInterruptions) {
    return <Maintenance />;
  }

  return (
    <div id="root-node" className="flex min-h-screen font-body ">
      {allowCompanionOptIn && !inPipMode && !inPimsPalRoute && (
        <CompanionSidebar />
      )}

      <PipDialog />
      {inPipMode && <MinimizedPipOverlay />}

      <div
        className="flex flex-col flex-1 max-w-full"
        id="container-node"
      >
        {!location.pathname.startsWith("/onboarding") &&
          !inPipMode &&
          !inPimsPalRoute &&
          isAppHeaderVisible && <Header />}
        <main className={mainClassNameOriginal}>
          {width < 500 && !inPipMode && !inPimsPalRoute && (
            <PermanentlyHideableBanner>
              <div className="flex flex-row items-center space-x-4">
                <a
                  href={getAppDownloadLink()}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="w-16">
                    <img
                      src={AppIconImg}
                      className="cover rounded-xl w-full"
                    />
                  </div>
                </a>
                <span className="text-left">
                  We&#39;ve detected you&#39;re using Scribenote on a
                  mobile device. <br /> For a more optimized mobile
                  experience, tap the icon to download our mobile app.
                </span>
              </div>
            </PermanentlyHideableBanner>
          )}
          {isUpdateAvailable && !shouldPreventInterruptions && (
            <Banner isWarning>
              A new version of Scribenote is available.{" "}
              <DSLinkButton
                dataTestId="version-check-reload-btn"
                data-mx-label="Version Update Reload"
                onClick={() => window.location.reload()}
              >
                Reload to update.
              </DSLinkButton>
              <br />
              <DSLinkButton
                dataTestId="version-check-changelog-btn"
                data-mx-label="Version Update Changelog"
                href="https://scribenote.com/changelog"
                target="_blank"
                rel="noopener noreferrer"
              >
                See what&apos;s new
              </DSLinkButton>
            </Banner>
          )}
          <AreYouSureModal
            shouldShowAreYouSureModal={shouldShowAreYouSureModal}
            hideAreYouSureModal={hideAreYouSureModal}
            confirmAction={confirmAction}
          />

          {releaseWebEnableFtueTour && <FirstTimeUserTour />}
          <AnalyticsRouter>
            <Routes>
              {/* PUBLIC & AUTH ROUTES */}
              <Route path="/" element={<Login />} />
              <Route path="/auth/login" element={<Login />} />
              <Route path="/sso/login" element={<SSOLogin />} />
              <Route
                path="/sso/login/descope"
                element={<SSODescopeLogin />}
              />
              <Route path="/sso/logout" element={<SSOLogout />} />
              <Route path="/sso/error" element={<SSOError />} />
              <Route path="/auth/register" element={<Register />} />
              <Route
                path="/auth/register/team/signup/:accessToken"
                element={<RegisterTeamInvite />}
              />
              <Route
                path="/teams/accept/:inviteUuid"
                element={<ExistingUserAcceptTeamInvite />}
              />
              <Route
                path="/forgot_password"
                element={<ForgotPassword />}
              />
              <Route
                path="/reset_password/:resetToken"
                element={<ForgotPasswordReset />}
              />
              <Route
                path="/registration_confirmation"
                element={
                  <PrivateRoute>
                    <RegisterConfirm />
                  </PrivateRoute>
                }
              />

              {/* END AUTH ROUTES */}

              {/* PUBLIC SHARED TEMPLATE ROUTE */}
              <Route
                path="/templates/shared/:templateUuid"
                element={<SharedTemplatePage />}
              />

              <Route
                path="/account"
                element={
                  <PrivateRoute>
                    <UploadProgressProvider>
                      <UserAccount />
                    </UploadProgressProvider>
                  </PrivateRoute>
                }
              />

              {/* CLINICAL ROUTES */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <OnboardingProvider>
                      <UserDashboard />
                    </OnboardingProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/onboarding/*"
                element={
                  <PrivateRoute>
                    <OnboardingProvider>
                      <RoleProvider>
                        <Onboarding />
                      </RoleProvider>
                    </OnboardingProvider>
                  </PrivateRoute>
                }
              />

              <Route
                path="/inbox"
                element={
                  <PrivateRoute>
                    <Inbox />
                  </PrivateRoute>
                }
              />
              {useLegacyNotes && (
                <Route
                  path="/typed_templates"
                  element={
                    <PrivateRoute>
                      <TypedTemplates />
                    </PrivateRoute>
                  }
                />
              )}
              {useLegacyNotes && (
                <Route
                  path="/typed_templates/new"
                  element={
                    <PrivateRoute>
                      <SuperSoapProvider>
                        <NewTypedTemplate />
                      </SuperSoapProvider>
                    </PrivateRoute>
                  }
                />
              )}
              {useLegacyNotes && (
                <Route
                  path="/typed_templates/edit/:uuid"
                  element={
                    <PrivateRoute>
                      <SuperSoapProvider>
                        <EditTypedTemplate />
                      </SuperSoapProvider>
                    </PrivateRoute>
                  }
                />
              )}
              <Route
                path="/templates"
                element={
                  <PrivateRoute>
                    <AutoTemplatesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/templates/edit/:uuid"
                element={
                  <PrivateRoute>
                    <AutoTemplateForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notebook"
                element={
                  <PrivateRoute>
                    <Patients />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notebook/:patientUUID"
                element={
                  <PrivateRoute>
                    <Notebook />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notebook/:patientUUID/keyinfo"
                element={
                  <PrivateRoute>
                    <KeyPatientInfo />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notebook/:patientUUID/attachments"
                element={
                  <PrivateRoute>
                    <UploadProgressProvider>
                      <Attachments />
                    </UploadProgressProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/notebook/analytics"
                element={
                  <PrivateRoute>
                    <PatientAnalytics />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notebook_select/move/:noteUUIDs"
                element={
                  <PrivateRoute>
                    <MovePatientSelect />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notebook_select/copy/:noteUUIDs"
                element={
                  <PrivateRoute>
                    <CopyPatientSelect />
                  </PrivateRoute>
                }
              />
              <Route
                path="/assistant_export_note/:noteAccessToken"
                element={<SharedExportNote />}
              />
              <Route
                path="/shared_export_note/:noteAccessToken"
                element={<SharedExportNote />}
              />
              <Route
                path="/audit_trail/note/:noteUUID"
                element={
                  <PrivateRoute>
                    <AuditTrailNote />
                  </PrivateRoute>
                }
              />
              <Route
                path="/audit_trail/patient/:patientUUID"
                element={
                  <PrivateRoute>
                    <AuditTrailPatient />
                  </PrivateRoute>
                }
              />
              <Route
                path="/audit_trail/user"
                element={
                  <PrivateRoute>
                    <AuditTrailUser />
                  </PrivateRoute>
                }
              />
              <Route
                path="/edit_note/:noteUUID"
                element={
                  <PrivateRoute>
                    <SuperSoapProvider>
                      <EditNote />
                    </SuperSoapProvider>
                  </PrivateRoute>
                }
              />
              {useLegacyNotes && (
                <Route
                  path="/new_note/select"
                  element={
                    <PrivateRoute>
                      <CreateNoteSelect />
                    </PrivateRoute>
                  }
                />
              )}
              <Route
                path="/new_note/patient/:patientUUID"
                element={
                  <PrivateRoute>
                    <CreateNote />
                  </PrivateRoute>
                }
              />
              {useLegacyNotes && (
                <Route
                  path="/new_note/quicknote"
                  element={
                    <PrivateRoute>
                      <CreateNote />
                    </PrivateRoute>
                  }
                />
              )}
              <Route
                path="/teams"
                element={
                  <PrivateRoute>
                    <TeamsManagementPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/teams/:teamUuid"
                element={
                  <PrivateRoute>
                    <TeamManagement />
                  </PrivateRoute>
                }
              />

              {/* PiP mode routes */}
              <Route
                path="/pip/notes"
                element={
                  <PrivateRoute>
                    <PipNoteList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/pip/note/:noteUuid"
                element={
                  <PrivateRoute>
                    <PipNote />
                  </PrivateRoute>
                }
              />

              {/* PIMSPal-specific routes */}
              <Route
                path="/pimspal/record"
                element={
                  <PrivateRoute>
                    <PimsPalRecord />
                  </PrivateRoute>
                }
              />

              {/* Design System Test Page (dev/qa only) */}
              {isTestEnv && (
                <Route
                  path="/design-system"
                  element={<DesignSystemTestPage />}
                />
              )}

              {/* All routes must be above this 404 or they will redirect. */}
              <Route path="*" element={<FourOhFour />} />
            </Routes>
          </AnalyticsRouter>

          {props.children}
        </main>
        {location.pathname.startsWith("/onboarding") ||
        inPipMode ? null : (
          <Footer />
        )}
      </div>
    </div>
  );
}
export default App;
